import { useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PreviewFeature } from 'types/PreviewFeature'
import AuthEventService from '../services/authEventService'
import { PreviewFeaturesService } from '../services/previewFeaturesService'

export interface UsePreviewFeaturesReturn {
    /** Enabled preview features */
    features: PreviewFeature[]
    /** Loading state */
    loading: boolean
    /** Error if fetch failed */
    error?: Error
    /** Check if feature is enabled */
    hasFeature: (featureName: string) => boolean
    /** Force refresh from API */
    refetch: () => void
    /** Pre-computed feature flags */
    previewFeatures: {
        hasNewWorkspace2: boolean
        hasLosAutomation: boolean
        hasPTAutomation: boolean
        hasOMWorkspace: boolean
        hasOMPTLinkAutomation: boolean
        hasHarmonyHeader: boolean
        hasProjectTasks: boolean
    }
}

/**
 * Hook for accessing preview features with automatic cache management.
 * Features are cached per session and auto-refresh on login/logout.
 *
 * @example
 * const { hasFeature, previewFeatures } = usePreviewFeatures()
 * if (previewFeatures.hasNewWorkspace2) { ... }
 *
 * @returns Preview features state and utilities
 */
export const usePreviewFeatures = (): UsePreviewFeaturesReturn => {
    const apolloClient = useApolloClient()
    const [features, setFeatures] = useState<PreviewFeature[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error>()
    const [refreshKey, setRefreshKey] = useState(0)
    const isMountedRef = useRef(true)

    // Subscribe to auth events to force refetch on logout/login
    useEffect(() => {
        const unsubscribe = AuthEventService.subscribe((event) => {
            if (event === 'logout' || event === 'login') {
                // Force refetch by changing the refresh key
                setRefreshKey((prev) => prev + 1)
            }
        })

        return () => {
            unsubscribe()
        }
    }, [])

    useEffect(() => {
        // Only the first load shows a loading state. Login/logout refreshes happen
        // in the background so an already-resolved header doesn't flash.
        const shouldForceRefresh = refreshKey > 0

        const fetchFeatures = async () => {
            try {
                const fetchedFeatures = shouldForceRefresh
                    ? await PreviewFeaturesService.forceRefresh(apolloClient)
                    : await PreviewFeaturesService.getFeatures(apolloClient)

                if (isMountedRef.current) {
                    setFeatures(fetchedFeatures)
                    setError(undefined)
                }
            } catch (err) {
                if (isMountedRef.current) {
                    setError(
                        err instanceof Error ? err : new Error('Failed to load')
                    )
                    setFeatures([])
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false)
                }
            }
        }

        fetchFeatures()
    }, [apolloClient, refreshKey])

    const refetch = useCallback(() => {
        setLoading(true)
        setError(undefined)

        PreviewFeaturesService.forceRefresh(apolloClient)
            .then((fetchedFeatures) => {
                if (isMountedRef.current) {
                    setFeatures(fetchedFeatures)
                }
            })
            .catch((err) => {
                if (isMountedRef.current) {
                    setError(
                        err instanceof Error
                            ? err
                            : new Error('Failed to refresh')
                    )
                }
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setLoading(false)
                }
            })
    }, [apolloClient])

    const hasFeature = useCallback(
        (featureName: string) =>
            features.includes(featureName as PreviewFeature),
        [features]
    )

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    return {
        features,
        loading,
        error,
        hasFeature,
        refetch,
        previewFeatures: {
            hasNewWorkspace2: hasFeature(PreviewFeature.NEW_WORKSPACE_20),
            hasLosAutomation: hasFeature(PreviewFeature.LOS_AUTOMATION),
            hasPTAutomation: hasFeature(PreviewFeature.PT_AUTOMATION),
            hasOMWorkspace: hasFeature(PreviewFeature.OM_WORKSPACE),
            hasOMPTLinkAutomation:
                hasFeature(PreviewFeature.OM_WORKSPACE) &&
                hasFeature(PreviewFeature.OM_PT_LINK_AUTOMATION),
            hasHarmonyHeader: hasFeature(PreviewFeature.APP_HARMONY_HEADER),
            hasProjectTasks: hasFeature(PreviewFeature.PROJECT_TASKS),
        },
    }
}
